import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  isSignal,
  NgZone,
  signal,
} from '@angular/core';
import { Location, NgTemplateOutlet } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  MobileChip,
  MobileHeaderService,
  PreloaderColor,
  UiBottomSheetComponent,
  UiMobileHeaderComponent,
  UiPreloaderComponent,
  UiTouchFeedbackDirective,
  ViewportService,
} from '@ui';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { MobileNavDrawerComponent } from '../mobile-nav-drawer/mobile-nav-drawer.component';
import { AppState } from '../../store/app.state';
import { routeFeature } from '../../store/state/route/route.feature';
import { BackCloseDirective } from '../../directives/back-close.directive';

const DEFAULT_ACTIONS_SHEET_TITLE = 'COMMON.ACTIONS';
const DELETE_ACTION_ICON = 'ui-icon-delete';
const DANGER_ACTIONS_GROUP = 'danger';

@Component({
  selector: 'app-mobile-header-outlet',
  standalone: true,
  imports: [
    UiMobileHeaderComponent,
    MobileNavDrawerComponent,
    UiPreloaderComponent,
    NgTemplateOutlet,
    MatTooltipModule,
    UiBottomSheetComponent,
    UiTouchFeedbackDirective,
    TranslatePipe,
    BackCloseDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mobile-header-outlet.component.html',
  styleUrl: './mobile-header-outlet.component.scss',
  animations: [
    trigger('fadeIn', [
      state('in', style({ opacity: 1 })),
      transition('void => in', [animate('150ms ease-in')]),
    ]),
  ],
})
export class MobileHeaderOutletComponent {
  protected readonly mobileHeader = inject(MobileHeaderService);
  private readonly viewport = inject(ViewportService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  protected readonly isTouchDevice = this.viewport.isTouchDevice;
  protected readonly PreloaderColor = PreloaderColor;
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private actionLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private actionLongPressFired = false;

  constructor() {
    const scrollHandler = (e: Event): void => {
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      if (el.scrollHeight <= el.clientHeight) return;
      this.mobileHeader.reportScroll(el.scrollTop);
    };
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('scroll', scrollHandler, true);
    });
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('scroll', scrollHandler, true);
    });
  }

  protected readonly subBarVisible = signal(true);

  protected readonly resolvedSubBar = computed(() => {
    const tpl = this.mobileHeader.config()?.subBarTemplate;
    if (!tpl) return null;
    return isSignal(tpl) ? (tpl() ?? null) : tpl;
  });

  protected readonly subBarToggleable = computed(
    () => !!this.resolvedSubBar() && !this.mobileHeader.config()?.subBarPinned,
  );

  private readonly subBarResetEffect = effect(() => {
    this.resolvedSubBar();
    this.subBarVisible.set(true);
  });

  private readonly breadcrumbs = this.store.selectSignal(
    routeFeature.selectBreadcrumbs,
  );

  private readonly fallbackTitle = computed(() => {
    const crumbs = this.breadcrumbs() ?? [];
    return crumbs.length ? crumbs[crumbs.length - 1].name : '';
  });

  protected readonly visible = computed(
    () =>
      this.viewport.isMobile() &&
      (this.mobileHeader.hasActiveConfig() ||
        !!this.mobileHeader.selection() ||
        !!this.fallbackTitle()),
  );

  protected readonly shouldShowBack = computed(() => {
    const cfg = this.mobileHeader.config();
    if (cfg?.showBackButton !== undefined) return cfg.showBackButton;
    const crumbs = this.breadcrumbs() ?? [];
    return crumbs.length >= 2 && !!crumbs[crumbs.length - 2]?.route;
  });

  protected readonly resolvedTitle = computed(() => {
    const cfg = this.mobileHeader.config();
    if (cfg?.title) {
      const t = cfg.title;
      const value = typeof t === 'string' ? t : t();
      if (value) return value;
    }
    if (cfg?.titleEditable) return '';
    return this.fallbackTitle();
  });

  protected readonly resolvedSubtitle = computed(() => {
    const cfg = this.mobileHeader.config();
    if (!cfg?.subtitle) return '';
    const s = cfg.subtitle;
    return typeof s === 'string' ? s : s();
  });

  protected readonly resolvedSubtitleTemplate = computed(() => {
    const tpl = this.mobileHeader.config()?.subtitleTemplate;
    if (!tpl) return null;
    return isSignal(tpl) ? (tpl() ?? null) : tpl;
  });

  protected readonly resolvedSearchPlaceholder = computed(() => {
    const cfg = this.mobileHeader.config();
    if (!cfg?.searchPlaceholder) return '';
    const sp = cfg.searchPlaceholder;
    return typeof sp === 'string' ? sp : sp();
  });

  protected readonly resolvedSearchQuery = computed(
    () => this.mobileHeader.config()?.searchQuery?.() ?? '',
  );

  protected readonly resolvedFilterDefs = computed(
    () => this.mobileHeader.config()?.filterDefs?.() ?? [],
  );

  protected readonly resolvedActiveFilters = computed(
    () => this.mobileHeader.config()?.activeFilters?.() ?? {},
  );

  protected readonly resolvedChips = computed(
    () => this.mobileHeader.config()?.chips?.() ?? [],
  );

  protected readonly resolvedTitleEditable = computed(
    () => this.mobileHeader.config()?.titleEditable ?? false,
  );

  protected readonly resolvedTitlePlaceholder = computed(
    () => this.mobileHeader.config()?.titlePlaceholder ?? '',
  );

  protected readonly resolvedShowFilterTrigger = computed(() => {
    const cfg = this.mobileHeader.config();
    const trigger = cfg?.showFilterTrigger;
    if (trigger !== undefined) {
      return typeof trigger === 'function' ? trigger() : trigger;
    }
    return !!cfg?.filterDefs?.()?.length || !!this.resolvedSearchPlaceholder();
  });

  protected readonly resolvedInitialFilterMode = computed(
    () => this.mobileHeader.config()?.initialFilterMode ?? false,
  );

  private readonly visibleActions = computed(() => {
    const actionsRaw = this.mobileHeader.config()?.actions;
    const actions = actionsRaw
      ? typeof actionsRaw === 'function'
        ? actionsRaw()
        : actionsRaw
      : [];
    return actions.filter((action) => {
      if (action.hidden !== undefined) {
        const isHidden = isSignal(action.hidden)
          ? action.hidden()
          : action.hidden;
        if (isHidden) return false;
      }
      return true;
    });
  });

  protected readonly resolvedActionsTemplate = computed(() => {
    const tpl = this.mobileHeader.config()?.actionsTemplate;
    if (!tpl) return null;
    return isSignal(tpl) ? (tpl() ?? null) : tpl;
  });

  private static readonly STYLED_CLASS_MAP: Record<string, string> = {
    primary: 'mobile-header-add-btn',
    danger: 'mobile-header-danger-btn',
  };

  private readonly resolvedActions = computed(() => {
    return this.visibleActions().map((action) => {
      const isDisabled =
        action.disabled !== undefined
          ? isSignal(action.disabled)
            ? action.disabled()
            : action.disabled
          : false;

      const isLoading =
        action.loading !== undefined
          ? isSignal(action.loading)
            ? action.loading()
            : action.loading
          : false;

      const tooltip =
        action.tooltip !== undefined
          ? isSignal(action.tooltip)
            ? action.tooltip()
            : action.tooltip
          : undefined;

      const styled = action.styled === true ? 'primary' : action.styled;
      const styledKey = typeof styled === 'string' ? styled : '';
      const styledClass =
        MobileHeaderOutletComponent.STYLED_CLASS_MAP[styledKey] ??
        'mobile-header-action-btn';

      return {
        ...action,
        tooltip,
        isDisabled,
        isLoading,
        isEffectivelyDisabled: isDisabled || isLoading,
        styledClass,
        isDanger:
          action.styled === 'danger' || action.icon === DELETE_ACTION_ICON,
      };
    });
  });

  protected readonly inlineActions = computed(() =>
    this.resolvedActions().filter((a) => !a.overflowMenu),
  );

  protected readonly overflowActions = computed(() =>
    this.resolvedActions().filter((a) => a.overflowMenu),
  );

  protected readonly overflowGroups = computed(() => {
    const actions = this.overflowActions();
    const groupKey = (action: (typeof actions)[number]): string | undefined =>
      action.group ?? (action.isDanger ? DANGER_ACTIONS_GROUP : undefined);
    return actions.reduce(
      (groups, action, index) => {
        if (index === 0 || groupKey(action) !== groupKey(actions[index - 1])) {
          groups.push([action]);
        } else {
          groups[groups.length - 1].push(action);
        }
        return groups;
      },
      [] as (typeof actions)[],
    );
  });

  protected readonly resolvedActionsSheetTitle = computed(() => {
    const title = this.mobileHeader.config()?.actionsSheetTitle;
    const resolved = isSignal(title) ? title() : title;
    return resolved?.length ? resolved : DEFAULT_ACTIONS_SHEET_TITLE;
  });

  protected readonly resolvedActionsSheetSubtitle = computed(() => {
    const subtitle = this.mobileHeader.config()?.actionsSheetSubtitle;
    return (isSignal(subtitle) ? subtitle() : subtitle) ?? '';
  });

  protected readonly overflowSheetOpen = signal(false);

  public toggleSubBar(): void {
    this.subBarVisible.update((v) => !v);
  }

  public onOverflowActionClick(action: {
    isEffectivelyDisabled: boolean;
    handler: () => void;
  }): void {
    if (!action.isEffectivelyDisabled) {
      this.overflowSheetOpen.set(false);
      action.handler();
    }
  }

  public onActionClick(action: {
    isEffectivelyDisabled: boolean;
    handler: () => void;
  }): void {
    if (this.actionLongPressFired) {
      this.actionLongPressFired = false;
      return;
    }
    if (!action.isEffectivelyDisabled) {
      action.handler();
    }
  }

  public onActionTouchStart(tooltip: MatTooltip): void {
    this.actionLongPressFired = false;
    this.actionLongPressTimer = setTimeout(() => {
      this.actionLongPressFired = true;
      tooltip.show(0);
    }, 500);
  }

  public onActionTouchEnd(tooltip: MatTooltip): void {
    if (this.actionLongPressTimer) {
      clearTimeout(this.actionLongPressTimer);
      this.actionLongPressTimer = null;
    }
    if (this.actionLongPressFired) {
      setTimeout(() => tooltip.hide(), 1500);
    }
  }

  public onBackClick(): void {
    const cfg = this.mobileHeader.config();
    if (cfg?.onBackClick) {
      cfg.onBackClick();
      return;
    }
    const crumbs = this.breadcrumbs() ?? [];
    if (crumbs.length >= 2) {
      const parent = crumbs[crumbs.length - 2];
      if (parent.route) {
        void this.router.navigate([parent.route], {
          queryParams: parent.queryParams,
          fragment: parent.fragment,
        });
        return;
      }
    }
    this.location.back();
  }

  public onSearchChange(query: string): void {
    this.mobileHeader.config()?.onSearchChange?.(query);
  }

  public onSearchSubmit(query: string): void {
    this.mobileHeader.config()?.onSearchSubmit?.(query);
  }

  public onFilterApply(ev: { key: string; values: unknown[] }): void {
    this.mobileHeader.config()?.onFilterApply?.(ev);
  }

  public onCustomPillTap(key: string): void {
    this.mobileHeader.config()?.onCustomPillTap?.(key);
  }

  public onChipRemove(chip: MobileChip): void {
    this.mobileHeader.config()?.onChipRemove?.(chip);
  }

  public onClearAll(): void {
    this.mobileHeader.config()?.onClearAll?.();
  }

  public onTitleDropdownClick(): void {
    this.mobileHeader.config()?.onTitleDropdownClick?.();
  }

  public onTitleChange(newTitle: string): void {
    this.mobileHeader.config()?.onTitleChange?.(newTitle);
  }
}
